import { useMemo, useState } from 'react';
import { Download, FileSpreadsheet, FileText, RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { listEmitentes } from '../api/emitente';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';
import LoadingSpinner from '../components/LoadingSpinner';

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function reportUrl(emitenteId: number, formato: 'pdf' | 'csv', ini: string, fim: string) {
  const qs = new URLSearchParams();
  if (ini) qs.set('ini', ini);
  if (fim) qs.set('fim', fim);
  const query = qs.toString();
  return `/api/emitentes/${emitenteId}/relatorio.${formato}${query ? `?${query}` : ''}`;
}

export default function RelatoriosPage() {
  const { emitenteId, setEmitenteId } = useEmitenteAtivo();
  const today = useMemo(() => new Date(), []);
  const firstDay = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
  const [ini, setIni] = useState(isoDate(firstDay));
  const [fim, setFim] = useState(isoDate(today));

  const emitentesQ = useQuery({
    queryKey: ['emitentes'],
    queryFn: listEmitentes,
    refetchOnWindowFocus: false,
  });

  const emitente = emitentesQ.data?.find((e) => e.id === emitenteId) || null;
  const periodoLabel = ini || fim ? `${ini || 'inicio'} a ${fim || 'fim'}` : 'todos os documentos';

  function baixar(formato: 'pdf' | 'csv') {
    if (!emitenteId) return;
    window.open(reportUrl(emitenteId, formato, ini, fim), '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="eyebrow">Conferência fiscal</div>
          <h1 className="text-xl md:text-2xl font-semibold">Relatórios</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Faturamento, cancelamentos e exportação operacional por período.
          </p>
        </div>
        <button className="btn-secondary" onClick={() => emitentesQ.refetch()} disabled={emitentesQ.isFetching}>
          <RefreshCw className={`h-4 w-4 ${emitentesQ.isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </header>

      <section className="card-surface p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="field-label">Emitente</label>
            {emitentesQ.isLoading ? (
              <div className="field-input flex items-center"><LoadingSpinner /></div>
            ) : (
              <select
                className="field-input"
                value={emitenteId || ''}
                onChange={(e) => setEmitenteId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Selecione</option>
                {(emitentesQ.data || []).map((e) => (
                  <option key={e.id} value={e.id}>{e.razao_social}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="field-label">Inicio</label>
            <input className="field-input" type="date" value={ini} onChange={(e) => setIni(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Fim</label>
            <input className="field-input" type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-1 dark:border-white/10 dark:bg-white/[0.02] dark:shadow-none">
            <div className="eyebrow">Período</div>
            <div className="mt-1 text-lg font-semibold">{periodoLabel}</div>
            <div className="mt-1 text-xs text-neutral-500 truncate">{emitente?.razao_social || 'Nenhum emitente selecionado'}</div>
          </div>
          <button
            type="button"
            className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 p-4 text-left hover:bg-emerald-500/15 disabled:opacity-50 disabled:hover:bg-emerald-500/10"
            disabled={!emitenteId}
            onClick={() => baixar('pdf')}
          >
            <div className="flex items-center gap-2 text-emerald-700 font-semibold dark:text-emerald-200">
              <FileText className="h-5 w-5" />
              Relatório PDF
            </div>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              Lista faturadas e canceladas, com totalização do período.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300">
              <Download className="h-4 w-4" />
              Baixar PDF
            </div>
          </button>
          <button
            type="button"
            className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-left transition hover:bg-sky-100 disabled:opacity-50 disabled:hover:bg-sky-50 dark:border-sky-500/25 dark:bg-sky-500/10 dark:hover:bg-sky-500/15 dark:disabled:hover:bg-sky-500/10"
            disabled={!emitenteId}
            onClick={() => baixar('csv')}
          >
            <div className="flex items-center gap-2 text-sky-800 font-semibold dark:text-sky-200">
              <FileSpreadsheet className="h-5 w-5" />
              Exportação CSV
            </div>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              Exporta NF-e, CT-e, CT-e OS e MDF-e com status, cStat, chave, valor, XML e PDF.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-sky-700 dark:text-sky-300">
              <Download className="h-4 w-4" />
              Baixar CSV
            </div>
          </button>
        </div>
      </section>
    </div>
  );
}
